import { IconInfoCircle } from "@tabler/icons-react";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import {
  GOALS,
  MODES,
  PRODUCT_STAGES,
  RANKS,
  SALES_GOAL,
  type useStrategyWizard,
} from "./use-strategy-wizard";

const GOAL_HINTS = [
  "扩大销售额，并设定目标小类排名",
  "优先提高经营利润",
  "争取更高的小类排名",
  "提高广告投入产出比",
  "改善商品评论表现",
];
const PRODUCT_STAGE_HINTS = [
  "商品刚上架，正在验证定位",
  "销量增长，逐步扩大有效流量",
  "经营稳定，关注效率与利润",
];
const MODE_HINTS = ["后续调整建议需你确认后执行", "后续在约定范围内自动执行调整"];

export function SettingsStep({ wizard }: { wizard: ReturnType<typeof useStrategyWizard> }) {
  return (
    <form
      id="strategy-settings"
      onSubmit={wizard.generatePlan}
      noValidate
      className="flex flex-col gap-6"
    >
      <div>
        <h2 className="text-lg font-semibold">这次运营，你最想实现什么？</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">
          结合商品分析资料设置业务目标，生成后可继续调整。
        </p>
      </div>
      {wizard.planSignature && !wizard.validPlan && (
        <p className="text-sm text-warning-foreground" role="status">
          运营目标已修改，请重新生成策略决策。
        </p>
      )}
      <ChoiceGroup
        label="商品阶段"
        value={wizard.settings.productStage}
        options={PRODUCT_STAGES}
        hints={PRODUCT_STAGE_HINTS}
        onChange={(value) => wizard.changeSetting("productStage", value)}
        error={wizard.settingsForm.formState.errors.productStage?.message}
      />
      <ChoiceGroup
        label="业务目标"
        value={wizard.settings.goal}
        options={GOALS}
        hints={GOAL_HINTS}
        optionsClassName="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
        onChange={(value) => wizard.changeSetting("goal", value)}
        error={wizard.settingsForm.formState.errors.goal?.message}
      />
      {wizard.settings.goal === SALES_GOAL && (
        <ChoiceGroup
          label="目标小类排名"
          value={wizard.settings.rank}
          options={RANKS}
          onChange={(value) => wizard.changeSetting("rank", value)}
          error={wizard.settingsForm.formState.errors.rank?.message}
        />
      )}
      <ChoiceGroup
        label="执行模式"
        value={wizard.settings.mode}
        options={MODES}
        hints={MODE_HINTS}
        onChange={(value) => wizard.changeSetting("mode", value)}
        error={wizard.settingsForm.formState.errors.mode?.message}
      />
      <p className="flex items-center gap-2 rounded-lg bg-muted/50 p-4 text-[13px] text-muted-foreground">
        <IconInfoCircle className="size-4 shrink-0" aria-hidden />
        调整建议和执行范围以实际授权与后续确认为准；当前仅保存运营方案。
      </p>
    </form>
  );
}

function ChoiceGroup({
  label,
  value,
  options,
  hints,
  optionsClassName,
  onChange,
  error,
}: {
  label: string;
  value: string;
  options: readonly string[];
  hints?: readonly string[];
  optionsClassName?: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <FieldSet>
      <FieldLegend variant="label">{label}</FieldLegend>
      <RadioGroup
        aria-label={label}
        value={value}
        onValueChange={(next) => {
          if (typeof next === "string") onChange(next);
        }}
        className={optionsClassName ?? "flex-row gap-3"}
      >
        {options.map((option, i) => (
          <label
            key={option}
            htmlFor={`${label}-${i}`}
            className={cn(
              "flex flex-1 cursor-pointer items-center gap-3 rounded-[10px] border p-4",
              value === option ? "border-primary bg-muted/50" : "border-line",
            )}
          >
            <Radio id={`${label}-${i}`} value={option} aria-label={option} />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-medium">{option}</span>
              {hints?.[i] && (
                <span className="text-[13px] leading-[21px] text-muted-foreground">{hints[i]}</span>
              )}
            </span>
          </label>
        ))}
      </RadioGroup>
      {error && (
        <p role="alert" className="text-xs text-destructive-foreground">
          {error}
        </p>
      )}
    </FieldSet>
  );
}
